import React, {
  // useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  checksheetGetAllRecallRequests,
  checksheetGetProgressByYear,
} from 'apis/checksheetAPIs';
import { fetchDplColleges } from 'apis/dplAPIs';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faChevronDown,
  faCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import useServiceAuth from 'auth/useServiceAuth';
import { setYear } from 'state/slices/settingsSlice';
import { classNames } from 'utils';
import StaticData from './StaticData';
import LoadingSkeleton from './LoadingSkeleton';
import { Modal } from 'react-bootstrap';
import StatusDefinitions from './StatusDefinitions';
import { Link } from 'react-router-dom';

// const PROVOST = 'PROVOST';
// const COLLEGE = 'COLLEGE';
// const DEPARTMENT = 'DEPARTMENT';
// const DARS = 'DARS';

const ProgramSummaryRow = React.memo(
  ({
    college,
    values,
    requests,
    userIsProvost,
    userIsCollege,
    userCollege,
  }) => {
    const isSameCollege = userCollege.includes(college.acadOrgCode);

    const [isSummaryRowOpen, setSummaryRowOpen] = useState(false);

    // const handleKeyDown = useCallback(
    //   (e) => {
    //     if (e.key === 'Enter' || e.key === ' ') {
    //       e.preventDefault();
    //       setSummaryRowOpen(!isSummaryRowOpen);
    //     }
    //   },
    //   [isSummaryRowOpen]
    // );

    return (
      <div className="py-1 border-top border-gray-4">
        <div
          className={classNames(
            isSummaryRowOpen && 'fw-bold',
            'summary-row-head row mx-0'
          )}
          onClick={() => setSummaryRowOpen(!isSummaryRowOpen)}
          // onKeyDown={(e) => handleKeyDown(e)}
          role="button"
          // tabIndex={0}
        >
          <div
            className="col-auto fw-bold d-flex gap-1 ps-0"
            style={{ width: '25%' }}
          >
            <FontAwesomeIcon
              icon={faChevronDown}
              className={classNames(isSummaryRowOpen && 'rotate-180')}
              style={{ margin: '4px 0' }}
            />
            <div>{college.alphaSortDescription}</div>
          </div>
          <div className="col-auto text-end" style={{ width: '16%' }}>
            {values?.DEPARTMENT ?? '\u2014'}
          </div>
          <div className="col-auto text-end" style={{ width: '16%' }}>
            {values?.COLLEGE ?? '\u2014'}
          </div>
          <div className="col-auto text-end" style={{ width: '16%' }}>
            {values?.PROVOST ?? '\u2014'}
          </div>
          <div className="col-auto text-end" style={{ width: '13%' }}>
            {college.acadOrgCode !== 'OTHER'
              ? values?.DARS ?? '\u2014'
              : '\u2014'}
          </div>
          <div className="col-auto text-end pe-0" style={{ width: '14%' }}>
            {college.acadOrgCode !== 'OTHER'
              ? values?.COMPLETE ?? '\u2014'
              : '\u2014'}
          </div>
        </div>
        {!!requests?.length &&
          (userIsProvost || (userIsCollege && isSameCollege)) && (
            <Link
              className="button-link ms-3 d-flex gap-1 align-items-center my-1"
              style={{ width: 'fit-content' }}
              to={`/recall-requests?college=${college.acadOrgCode}`}
            >
              <div
                className="bg-asu-pink rounded-circle"
                style={{ width: '32px', height: '32px' }}
              >
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-white m-1"
                  style={{ width: '16px', height: '16px' }}
                />
              </div>
              {requests.length} recall requests
            </Link>
          )}
        <div
          className={classNames(
            isSummaryRowOpen && 'expansion-open',
            college.acadOrgCode === 'OTHER' && 'other-expansion',
            'summary-row-expansion'
          )}
        >
          {college.acadOrgCode !== 'OTHER' && (
            <>
              <div className="row mx-0 my-1">
                <div className="col-auto ps-0" style={{ width: '25%' }}>
                  <Link
                    to={`/programs?college=${college.acadOrgCode}&program=undergrad`}
                    className="ms-3 text-gray-7 underline-maroon"
                    style={{ width: 'fit-content' }}
                  >
                    Undergraduate degrees
                  </Link>
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {values?.majors?.DEPARTMENT ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {values?.majors?.COLLEGE ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {values?.majors?.PROVOST ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '13%' }}>
                  {values?.majors?.DARS ?? '\u2014'}
                </div>
                <div
                  className="col-auto text-end pe-0"
                  style={{ width: '14%' }}
                >
                  {values?.majors?.COMPLETE ?? '\u2014'}
                </div>
              </div>
              <div className="row mx-0 mb-1">
                <div className="col-auto ps-0" style={{ width: '25%' }}>
                  <Link
                    to={`/programs?college=${college.acadOrgCode}&program=minor`}
                    className="ms-3 text-gray-7 underline-maroon"
                    style={{ width: 'fit-content' }}
                  >
                    Minors
                  </Link>
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {values?.minors?.DEPARTMENT ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {values?.minors?.COLLEGE ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {values?.minors?.PROVOST ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '13%' }}>
                  {values?.minors?.DARS ?? '\u2014'}
                </div>
                <div
                  className="col-auto text-end pe-0"
                  style={{ width: '14%' }}
                >
                  {values?.minors?.COMPLETE ?? '\u2014'}
                </div>
              </div>
              <div className="row mx-0 mb-1">
                <div className="col-auto ps-0" style={{ width: '25%' }}>
                  <Link
                    to={`/programs?college=${college.acadOrgCode}&program=cert`}
                    className="ms-3 text-gray-7 underline-maroon"
                    style={{ width: 'fit-content' }}
                  >
                    Certificates
                  </Link>
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {values?.certs?.DEPARTMENT ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {values?.certs?.COLLEGE ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {values?.certs?.PROVOST ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '13%' }}>
                  {values?.certs?.DARS ?? '\u2014'}
                </div>
                <div
                  className="col-auto text-end pe-0"
                  style={{ width: '14%' }}
                >
                  {values?.certs?.COMPLETE ?? '\u2014'}
                </div>
              </div>
              {/* <div className="row mx-0 mb-1">
              <div className="col-auto ps-0" style={{ width: '25%' }}>
                <Link
                  to={`/programs?college=${college.acadOrgCode}&program=ids`}
                  className="ms-3 text-gray-7 underline-maroon"
                  style={{ width: 'fit-content' }}
                >
                  IDS Concentrations
                </Link>
              </div>
              <div className="col-auto text-end" style={{ width: '16%' }}>
                {values?.ids?.DEPARTMENT ?? '\u2014'}
              </div>
              <div className="col-auto text-end" style={{ width: '16%' }}>
                {values?.ids?.COLLEGE ?? '\u2014'}
              </div>
              <div className="col-auto text-end" style={{ width: '16%' }}>
                {values?.ids?.PROVOST ?? '\u2014'}
              </div>
              <div className="col-auto text-end" style={{ width: '13%' }}>
                {values?.ids?.DARS ?? '\u2014'}
              </div>
              <div className="col-auto text-end pe-0" style={{ width: '14%' }}>
                {values?.ids?.COMPLETE ?? '\u2014'}
              </div>
            </div> */}
            </>
          )}
          <div
            className={classNames(
              college.acadOrgCode === 'OTHER' && 'mt-1',
              'row mx-0 mb-1'
            )}
          >
            <div className="col-auto ps-0" style={{ width: '25%' }}>
              <Link
                to={`/programs?college=${college.acadOrgCode}&program=proposed`}
                className="ms-3 text-gray-7 underline-maroon"
                style={{ width: 'fit-content' }}
              >
                Proposed
              </Link>
            </div>
            <div className="col-auto text-end" style={{ width: '16%' }}>
              {values?.proposed?.DEPARTMENT ?? '\u2014'}
            </div>
            <div className="col-auto text-end" style={{ width: '16%' }}>
              {values?.proposed?.COLLEGE ?? '\u2014'}
            </div>
            <div className="col-auto text-end" style={{ width: '16%' }}>
              {values?.proposed?.PROVOST ?? '\u2014'}
            </div>
            <div className="col-auto text-end" style={{ width: '13%' }}>
              {'\u2014'}
            </div>
            <div className="col-auto text-end pe-0" style={{ width: '14%' }}>
              {'\u2014'}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const ProgramsSummary = () => {
  const { isAuthenticated, getAccessToken } = useServiceAuth();
  const dispatch = useDispatch();

  // const [departmentList, setDepartmentList] = useState([]);
  const [showDefinitions, setShowDefinition] = useState(false);
  const [requestsByCollege, setRequestsByCollege] = useState({});

  const year = useSelector((state) => state.settings.year);
  const role = useSelector((state) => state.user.role);
  const userCollege = useSelector((state) => state.user.college);
  // const userDepartment = useSelector((state) => state.user.department);

  const userIsProvost = role?.includes('PROVOST');
  const userIsCollege = role?.includes('COLLEGE');

  const {
    data: checksheetProgressData,
    error: getChecksheetProgressError,
    isError: getChecksheetProgressIsError,
    isPending: getChecksheetIsLoading,
    isSuccess: getChecksheetProgressIsSuccess,
  } = useQuery({
    queryKey: ['checksheet-progress', year],
    queryFn: async () => {
      const token = await getAccessToken();
      return checksheetGetProgressByYear({ year, token });
    },
    enabled: !!year,
  });

  const {
    data: fetchCollegesData,
    error: fetchCollegesError,
    isError: fetchCollegesIsError,
  } = useQuery({
    queryKey: ['colleges'],
    queryFn: fetchDplColleges,
  });

  const { data: recallRequestData, isPending: recallRequestIsLoading } =
    useQuery({
      queryKey: ['checksheetrecallrequests', year, JSON.parse(role)?.[0] ?? ''],
      queryFn: async () => {
        const token = await getAccessToken();
        const result = await checksheetGetAllRecallRequests({
          year,
          role: JSON.parse(role)[0],
          token,
        });
        return result;
      },
      enabled:
        isAuthenticated && !!year && !!role && (userIsProvost || userIsCollege),
    });

  const { data: recallRequestDarsData, isPending: recallRequestDarsIsLoading } =
    useQuery({
      queryKey: ['checksheetrecallrequests', year, 'DARS'],
      queryFn: async () => {
        const token = await getAccessToken();
        const result = await checksheetGetAllRecallRequests({
          year,
          role: 'DARS',
          token,
        });
        return result;
      },
      enabled: isAuthenticated && !!year && userIsProvost,
    });

  const handleSetYear = (e) => {
    dispatch(setYear(e.target.value));
  };

  const handleOpenDefinitions = () => setShowDefinition(true);
  const handleCloseDefinitions = () => setShowDefinition(false);

  useEffect(() => {
    if (!!recallRequestData?.body && !recallRequestIsLoading) {
      const requestsByCollegeObj = {};

      JSON.parse(recallRequestData.body).forEach((request) => {
        const splitId = request.checksheet.split('*');

        if (!requestsByCollegeObj[splitId[1]])
          requestsByCollegeObj[splitId[1]] = [request];
        else requestsByCollegeObj[splitId[1]].push(request);
      });

      if (
        userIsProvost &&
        !!recallRequestDarsData?.body &&
        !recallRequestDarsIsLoading
      ) {
        JSON.parse(recallRequestDarsData.body).forEach((request) => {
          const splitId = request.checksheet.split('*');

          if (!requestsByCollegeObj[splitId[1]])
            requestsByCollegeObj[splitId[1]] = [request];
          else requestsByCollegeObj[splitId[1]].push(request);
        });
      }

      setRequestsByCollege(requestsByCollegeObj);
    }
  }, [
    recallRequestDarsData,
    recallRequestDarsIsLoading,
    recallRequestData,
    recallRequestIsLoading,
    userIsProvost,
  ]);

  const collegeList = useMemo(() => {
    if (!fetchCollegesData) return [];
    return fetchCollegesData.toSorted((a, b) =>
      a.alphaSortDescription.localeCompare(b.alphaSortDescription)
    );
  }, [fetchCollegesData]);

  const { progresses, progressTotals } = useMemo(() => {
    if (!checksheetProgressData) return { progresses: {}, progressTotals: {} };
    const { totals, ...colleges } = checksheetProgressData;
    return { progresses: colleges, progressTotals: totals };
  }, [checksheetProgressData]);

  // useEffect(() => {
  //   if (role.includes(COLLEGE) || role.includes(DEPARTMENT)) {
  //     const userCollegeFound = collegeList.find(
  //       (college) => college.acadOrgCode === userCollege
  //     );

  //     if (userCollegeFound) {
  //       const next = [];

  //       userCollegeFound.childAcadOrgs.forEach((department) => {
  //         next.push(department);

  //         department.childAcadOrgs.forEach((childDepartment) => {
  //           next.push(childDepartment);
  //         });
  //       });

  //       setDepartmentList(next);
  //     }
  //   }
  // }, [collegeList, role, userCollege]);

  // useEffect(() => {
  //   if (!!collegeList) {
  //     const collegeFound = collegeList.find(
  //       (college) => college.acadOrgCode === userCollege
  //     );

  //     if (!!collegeFound) setCollegeName(collegeFound.description);
  //   }
  // }, [userCollege, collegeList]);

  // useEffect(() => {
  //   if (!!departmentList) {
  //     const departmentFound = departmentList.find(
  //       (department) => department.acadOrgCode === userDepartment
  //     );

  //     if (!!departmentFound) setDepartmentName(departmentFound.description);
  //   }
  // }, [userDepartment, departmentList]);

  if (getChecksheetProgressIsError || fetchCollegesIsError)
    return (
      <div>
        <div>
          An error has occurred:{' '}
          {getChecksheetProgressError?.message || fetchCollegesError?.message}
        </div>
        <div>
          Please refresh the page. If the error persists, please try again
          later.
        </div>
      </div>
    );

  return (
    <>
      <div className="bg-white border border-gray-3 p-6">
        <div className="d-flex gap-1 justify-content-between align-items-center mb-3">
          <div>
            <div className="d-flex">
              {/* {(role.includes(COLLEGE) || role.includes(DEPARTMENT)) && (
                <div className="fw-bold fs-5 lh-1 mb-1">
                  {role.includes(DEPARTMENT) && departmentName + ' | '}
                  {collegeName}
                </div>
              )} */}
            </div>
            <div className="fw-bold fs-1 lh-1">Programs summary</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="fw-bold">Catalog year</div>
            <select value={year} onChange={(e) => handleSetYear(e)}>
              {StaticData.yearList.map((year) => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="d-flex justify-content-end">
          <div
            className="button-link"
            onClick={handleOpenDefinitions}
            role="button"
          >
            What do these statuses mean?
          </div>
        </div>
        <div>
          <div className="row py-2 mx-0">
            <div
              className="col-auto d-flex align-items-end fw-bold ps-0"
              style={{ width: '25%' }}
            >
              <div>College / school</div>
            </div>
            <div
              className="col-auto fw-bold d-flex gap-1 justify-content-end"
              style={{ width: '16%' }}
            >
              <FontAwesomeIcon
                icon={faCircle}
                className="text-gray-4"
                width={'12px'}
                style={{ margin: '4px 0' }}
              />
              <div className="text-end" style={{ maxWidth: 'min-content' }}>
                Pending department submission
              </div>
            </div>
            <div
              className="col-auto fw-bold d-flex gap-3 justify-content-between"
              style={{ width: '16%' }}
            >
              <FontAwesomeIcon
                icon={faArrowRight}
                className="text-gray-4"
                width={'12px'}
              />
              <div className="d-flex gap-1">
                <FontAwesomeIcon
                  icon={faCircle}
                  className="text-gray-7"
                  width={'12px'}
                  style={{ margin: '4px 0' }}
                />
                <div className="text-end" style={{ maxWidth: 'min-content' }}>
                  Pending college / school submission
                </div>
              </div>
            </div>
            <div
              className="col-auto fw-bold d-flex gap-3 justify-content-between"
              style={{ width: '16%' }}
            >
              <FontAwesomeIcon
                icon={faArrowRight}
                className="text-gray-4"
                width={'12px'}
              />
              <div className="d-flex gap-1">
                <FontAwesomeIcon
                  icon={faCircle}
                  className="text-warning"
                  width={'12px'}
                  style={{ margin: '4px 0' }}
                />
                <div className="text-end" style={{ maxWidth: 'min-content' }}>
                  Pending provost review
                </div>
              </div>
            </div>

            <div className="col-auto text-center px-1" style={{ width: '3%' }}>
              <FontAwesomeIcon
                icon={faArrowRight}
                className="text-gray-4"
                width={'12px'}
              />
            </div>
            <div
              className="col-auto fw-bold d-flex gap-1 justify-content-end"
              style={{ width: '11%' }}
            >
              <FontAwesomeIcon
                icon={faCircle}
                className="text-info"
                width={'12px'}
                style={{ margin: '4px 0' }}
              />
              <div className="text-end" style={{ maxWidth: 'min-content' }}>
                Pending DARS encoding
              </div>
            </div>
            <div className="col-auto text-center px-1" style={{ width: '3%' }}>
              <FontAwesomeIcon
                icon={faArrowRight}
                className="text-gray-4"
                width={'12px'}
              />
            </div>
            <div
              className="col-auto fw-bold d-flex gap-1 justify-content-end pe-0"
              style={{ width: '10%' }}
            >
              <FontAwesomeIcon
                icon={faCircle}
                className="text-success"
                width={'12px'}
                style={{ margin: '4px 0' }}
              />
              <div className="text-end" style={{ maxWidth: 'min-content' }}>
                Completed
              </div>
            </div>
          </div>
          {getChecksheetIsLoading ? (
            <>
              {[...Array(10)].map((item, index) => (
                <LoadingSkeleton key={index} width={'100%'} height={'37px'} />
              ))}
            </>
          ) : (
            getChecksheetProgressIsSuccess && (
              <div>
                {/*  Filter method is temporary until I figure out a better way */}
                {collegeList
                  .filter(
                    (college) =>
                      college.acadOrgCode !== 'COE' &&
                      college.acadOrgCode !== 'CGC' &&
                      college.acadOrgCode !== 'CLW' &&
                      college.acadOrgCode !== 'CMED' &&
                      college.acadOrgCode !== 'CHO' &&
                      college.acadOrgCode !== 'CPROVOST'
                  )
                  .map((college, index) => (
                    <ProgramSummaryRow
                      key={index}
                      college={college}
                      values={progresses[college.acadOrgCode]}
                      requests={requestsByCollege[college.acadOrgCode]}
                      userIsProvost={userIsProvost}
                      userIsCollege={userIsCollege}
                      userCollege={userCollege}
                    />
                  ))}
                <ProgramSummaryRow
                  college={{
                    alphaSortDescription: 'Other',
                    acadOrgCode: 'OTHER',
                  }}
                  values={progresses['OTHER']}
                  requests={requestsByCollege['OTHER']}
                  userIsProvost={userIsProvost}
                  userIsCollege={userIsCollege}
                  userCollege={userCollege}
                />
              </div>
            )
          )}
          <div className="row pt-1 mx-0 border-top border-gray-7 border-2">
            {getChecksheetIsLoading ? (
              <LoadingSkeleton width={'100%'} height={'39px'} />
            ) : (
              <>
                <div
                  className="col-auto fw-bold d-flex gap-1 ps-0"
                  style={{ width: '25%' }}
                >
                  Total
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {progressTotals?.DEPARTMENT ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {progressTotals?.COLLEGE ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '16%' }}>
                  {progressTotals?.PROVOST ?? '\u2014'}
                </div>
                <div className="col-auto text-end" style={{ width: '13%' }}>
                  {progressTotals?.DARS ?? '\u2014'}
                </div>
                <div
                  className="col-auto text-end pe-0"
                  style={{ width: '14%' }}
                >
                  {progressTotals?.COMPLETE ?? '\u2014'}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Modal show={showDefinitions} onHide={handleCloseDefinitions}>
        <StatusDefinitions handleClose={handleCloseDefinitions} />
      </Modal>
    </>
  );
};

export default ProgramsSummary;
